import { Children, ReactElement, ReactNode, createContext, isValidElement, use, useMemo } from 'react';

import { Styles, css, sva } from '@flipster/styled-system/css';
import { center } from '@flipster/styled-system/patterns';

import { Spinner } from '../design-system/spinner';
import { NoData } from './no-data';

const py = 16;
const px = 8;
const edgePadding = 16;

export const tableSlotRecipe = sva({
  className: 'table',
  slots: ['root', 'thead', 'tbody', 'th', 'td', 'tr'],
  base: {
    root: {
      tableLayout: 'fixed',
      w: '100%',
      borderSpacing: 0,
      borderCollapse: 'collapse',
      textAlign: 'left',
    },
    thead: {
      color: 'content.default.level-2',
      '& tr': {
        borderColor: 'border.default.level-2',
      },
    },
    tbody: {
      color: 'content.default.level-4',
    },
    tr: {
      borderBottom: '1px solid',
      borderColor: 'border.default.level-1',
    },
    th: {
      py,
      px,
      _first: {
        pl: edgePadding,
      },
      _last: {
        pr: edgePadding,
      },
      bgColor: 'inherit',
      verticalAlign: 'middle',
    },
    td: {
      py,
      px,
      _first: {
        pl: edgePadding,
      },
      _last: {
        pr: edgePadding,
      },
      bgColor: 'inherit',
      verticalAlign: 'middle',
    },
  },
});

const TableContext = createContext<{
  slots: ReturnType<typeof tableSlotRecipe.raw>;
  isLoading: boolean;
}>({
  slots: tableSlotRecipe.raw(),
  isLoading: false,
});

interface TableBaseProps {
  children?: ReactNode;
  css?: Styles;
}

const TableBase = ({
  children,
  css: cssProps,
  isLoading,
  noData = <NoData />,
}: TableBaseProps & { isLoading?: boolean; noData?: ReactNode }) => {
  const slots = useMemo(() => tableSlotRecipe.raw(), []);

  const isEmpty = useMemo(
    () =>
      Children.toArray(children)
        .flatMap((child) =>
          isValidElement(child) && child.type === Body
            ? Children.toArray((child as ReactElement<{ children?: ReactNode }>).props.children)
            : [],
        )
        .filter((row) => isValidElement(row) && row.type === Row).length === 0,
    [children],
  );

  return (
    <TableContext value={{ slots, isLoading: Boolean(isLoading) }}>
      <table className={css(slots.root, cssProps)}>{children}</table>
      {isLoading && (
        <div className={center({ w: '100%', py: 120 })}>
          <Spinner size={24} />
        </div>
      )}
      {!isLoading && isEmpty && noData}
    </TableContext>
  );
};

const Header = ({ children, css: cssProps }: TableBaseProps) => {
  const { slots } = use(TableContext);
  return <thead className={css(slots.thead, cssProps)}>{children}</thead>;
};

const Body = ({ children, css: cssProps }: TableBaseProps) => {
  const { slots, isLoading } = use(TableContext);
  return <tbody className={css(slots.tbody, cssProps)}>{!isLoading && children}</tbody>;
};

const Row = ({
  children,
  css: cssProps,
  pointer,
  hoverColor,
}: TableBaseProps & { pointer?: boolean; hoverColor?: boolean }) => {
  const { slots } = use(TableContext);
  return (
    <tr
      className={css(
        slots.tr,
        cssProps,
        pointer && { cursor: 'pointer' },
        hoverColor && { _hover: { bgColor: 'background.default.level-1' } },
      )}
    >
      {children}
    </tr>
  );
};

const HeaderCell = ({
  children,
  css: cssProps,
  justify,
  minW,
  w,
}: TableBaseProps & { justify?: 'start' | 'center' | 'end'; minW?: number; w?: number }) => {
  const { slots } = use(TableContext);
  return (
    <th className={css(slots.th, cssProps, justifyToAlign(justify), !!minW && { minW }, !!w && { w })}>{children}</th>
  );
};

const BodyCell = ({ children, css: cssProps, justify }: TableBaseProps & { justify?: 'start' | 'center' | 'end' }) => {
  const { slots } = use(TableContext);
  return <td className={css(slots.td, cssProps, justifyToAlign(justify))}>{children}</td>;
};

// Sorter 추가 필요
export const Table = Object.assign(TableBase, {
  Header,
  Body,
  Row,
  HeaderCell,
  BodyCell,
});

const JustifyToAlignMapper = {
  start: 'left',
  center: 'center',
  end: 'right',
};

function justifyToAlign(justify: 'start' | 'center' | 'end' = 'start'): Styles {
  return { textAlign: JustifyToAlignMapper[justify] };
}
